import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { HotelCard } from '@/components/travel/hotel-card';
import { FilterRowHotels, type HotelFilters } from '@/components/search/FilterRow';
import { Pager } from '@/components/search/Pager';
import { PricingLegend } from '@/components/search/PricingLegend';
import { HotelMap } from '@/components/hotels/hotel-map';
// VOY-466: Match-My-Taste toggle. Optional render — only when the
// parent provides `onPersonalizeChange` (i.e. it wires the pipeline's
// setPersonalizeHotels back through). Back-compat: callers that don't
// thread the handler simply don't see the toggle.
import { MatchMyTasteToggle } from '@/components/search/MatchMyTasteToggle';
// Alias the Map icon so it doesn't shadow the native Map constructor
// (the hotelOrder useMemo below relies on `new Map<...>()`).
import { List, Map as MapIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useSubscriptionRulesSyncState } from '@/hooks/useSubscriptionRules';
import type { Hotel } from '@/types/travel';

interface RegionSuggestion {
  id: string | number;
  name: string;
  country_code?: string;
  lat?: number;
  lon?: number;
  type?: string;
}

interface HotelTabContentProps {
  hotels: Hotel[];
  hotelCheckin?: string;
  hotelCheckout?: string;
  hotelGuests?: Array<{ adults: number; children: number[] }>;
  hotelTotal?: number;
  hotelPage?: number;
  hotelPerPage?: number;
  hotelTotalPages?: number;
  onHotelPageChange?: (page: number) => void;
  suggestions?: RegionSuggestion[];
  widened?: Hotel[];
  onSelectSuggestion?: (s: RegionSuggestion) => void;
  /**
   * VOY-489 fix: filters are now controlled from the parent. Lifting
   * the state up prevents HotelTabContent's unmount/remount (between
   * the isLoadingHotels=true and =false branches in SearchResults)
   * from wiping selected filters.
   *
   * Optional with a default of {} for back-compat with the loading-
   * skeleton render that doesn't care about filter state.
   */
  filters?: HotelFilters;
  onFiltersChange?: (filters: HotelFilters) => void;
  isLoading?: boolean;
  viewMode?: 'list' | 'map' | 'both';
  onViewModeChange?: (mode: 'list' | 'map' | 'both') => void;
  /**
   * VOY-466: Match-My-Taste personalization controls. Lifted up to
   * LandingV6 (which owns the pipeline + Hotel.match_score state).
   * When `onPersonalizeChange` is provided, the toggle renders next
   * to the filter row; otherwise (back-compat for non-LandingV6
   * callers like loading-skeleton mounts) the toggle is hidden.
   */
  personalizeEnabled?: boolean;
  onPersonalizeChange?: (next: boolean) => void;
}

// Subscribe CTA Banner for non-subscribers.
// VOY-493 review: chrome now matches the shared `.sr-subscribe`
// banner used in FlightTabContent. Full dedup (replace this with
// `<SubscribeToBookInterstitial>`) is tracked as a separate
// VOY-489-followup chip — kept inline for now since the copy
// differs ("$2.49/mo (Basic). Annual billing." vs "$2.49 / month.").
function SubscribeCTABanner() {
  return (
    <div className="sr-subscribe" role="complementary" aria-label="Subscribe to Voy member pricing">
      <div className="sr-subscribe-copy">
        <h4>Subscribe to book at member prices</h4>
        <p>From $2.49/mo (Basic). Annual billing. Zero markup on every hotel booking.</p>
      </div>
      <Link
        to="/subscription"
        onClick={(e) => e.stopPropagation()}
        className="sr-subscribe-cta"
        aria-label="See subscription plans"
      >
        See plans
      </Link>
    </div>
  );
}

export function HotelTabContent({
  hotels,
  hotelCheckin,
  hotelCheckout,
  hotelGuests,
  hotelTotal,
  hotelPage,
  hotelPerPage,
  hotelTotalPages,
  onHotelPageChange,
  suggestions = [],
  widened = [],
  onSelectSuggestion,
  filters: filtersProp,
  onFiltersChange,
  isLoading = false,
  viewMode = 'list',
  onViewModeChange,
  personalizeEnabled = false,
  onPersonalizeChange,
}: HotelTabContentProps) {
  const { user } = useAuth();
  // VOY-512 (D3): this is a hotel surface — resolve config with the explicit 'hotel' product.
  // Only `rules.canHaveSpecialPrice` (tier-derived) is read today, so markup scope has no
  // visible effect here, but the explicit product keeps it correct if a markup read is added.
  // VOY-610: while the plan is unknown (loading, or its lookup failed) neither the tier badge
  // nor the upsell renders, so a paying user never shows as Free.
  const { rules, isPlanKnown } = useSubscriptionRulesSyncState({ product: 'hotel' });
  const isAuthenticated = !!user;
  const isSubscriber = rules.canHaveSpecialPrice;

  // VOY-489 fix: filters are now controlled from the parent (LandingV6
  // owns the hotelFilters state). The prior local useState here was
  // wiped every time HotelTabContent remounted between the
  // isLoadingHotels=true / =false branches in SearchResults — the
  // "1 star always" bug. Default to {} if uncontrolled (e.g., the
  // loading-skeleton render path).
  const filters = useMemo(() => filtersProp ?? {}, [filtersProp]);
  const [minPriceBound, setMinPriceBound] = useState(0);
  const [maxPriceBound, setMaxPriceBound] = useState(1000);
  const [availableAmenities, setAvailableAmenities] = useState<string[]>([]);
  const [hoveredHotelId, setHoveredHotelId] = useState<string | null>(null);
  // VOY-489: sortOpen / currentSort dropped — sort lives inside
  // FilterRowHotels now (filters.sortBy flows through the same
  // onChange path).
  const MAP_ENABLED = (import.meta.env?.VITE_ENABLE_HOTEL_MAP ?? 'true') !== 'false';
  const listPaneRef = useRef<HTMLDivElement | null>(null);
  // TODO(VOY-490-followup): "Search this area" button is not wired
  // yet — the state has no setter exposed, so the `searchThisArea &&
  // mapBounds` branch in hotelMapFilters / hotelMapHotels below is
  // effectively dead code. Keeping the plumbing in place so the
  // future "research in current map bounds" UI doesn't have to
  // re-thread state down. File a ticket before next epic close.
  const [searchThisArea] = useState(false);
  const [mapBounds, setMapBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);

  // VOY-490 fix (infinite-loop): the previous inline arrow
  // `onBoundsChanged={(b) => setMapBounds(b)}` created a new function
  // reference on every render. MapContent's bounds-emit useEffect
  // (in hotel-map.tsx) has `onBoundsChanged` in its deps, so it
  // re-ran every render, fired its initial `emit()` which called
  // setMapBounds, which re-rendered HotelTabContent, which made
  // another new arrow, etc. — "Maximum update depth exceeded" loop.
  // useCallback with stable deps fixes the loop AND lets the cluster
  // setup useEffect actually run to completion (which is why the
  // Both view was showing only base tiles — the data layers never
  // finished mounting under the thrash).
  const handleBoundsChanged = useCallback(
    (b: { north: number; south: number; east: number; west: number }) => setMapBounds(b),
    [],
  );

  // VOY-490 fix (loop guard #2): same shape problem on the `filters`
  // prop — the prior inline `{ ...filters, ...(searchThisArea && mapBounds ? { bounds: mapBounds } : {}) }`
  // produced a new object reference on every render even when none
  // of the inputs changed. MapContent's geojson useMemo + cluster
  // source useEffect cascade off `filters` shape, so a fresh ref
  // each render forces the source data to re-set every cycle. Wrap
  // in useMemo so identity stays stable when inputs are unchanged.
  const hotelMapFilters = useMemo(() => {
    if (searchThisArea && mapBounds) {
      return { ...filters, bounds: mapBounds };
    }
    return filters;
  }, [filters, searchThisArea, mapBounds]);

  // Same for the bounds-prefiltered hotels list passed to the Both
  // view's HotelMap.
  const hotelMapHotels = useMemo(() => {
    if (!(searchThisArea && mapBounds)) return hotels;
    const { north, south, east, west } = mapBounds;
    return hotels.filter(h =>
      h.coordinates &&
      h.coordinates.latitude <= north &&
      h.coordinates.latitude >= south &&
      h.coordinates.longitude <= east &&
      h.coordinates.longitude >= west,
    );
  }, [hotels, searchThisArea, mapBounds]);

  // Extract price bounds and amenities
  useEffect(() => {
    if (hotels.length > 0) {
      const allPrices = hotels.map(h => h.price?.amount).filter(Boolean) as number[];
      if (allPrices.length > 0) {
        setMinPriceBound(Math.floor(Math.min(...allPrices)));
        setMaxPriceBound(Math.ceil(Math.max(...allPrices)));
      }
      const allAmenities = new Set<string>();
      hotels.forEach(h => { h.amenities?.forEach(a => allAmenities.add(a)); });
      setAvailableAmenities(Array.from(allAmenities).sort());
    } else {
      setMinPriceBound(0);
      setMaxPriceBound(1000);
      setAvailableAmenities([]);
    }
  }, [hotels]);

  // VOY-489: filters now controlled from parent — handleFiltersChange
  // and handleClearFilters become thin wrappers that just emit upward.
  const handleFiltersChange = useCallback((newFilters: HotelFilters) => {
    onFiltersChange?.(newFilters);
  }, [onFiltersChange]);

  const handleClearFilters = useCallback(() => {
    onFiltersChange?.({});
  }, [onFiltersChange]);

  // VOY-489: handleSortChange dropped — FilterRowHotels emits the
  // sort change via the same `onChange` path as any other filter
  // (it sets filters.sortBy on the existing handler).

  const hasActiveFilters = Object.keys(filters).filter(k => k !== 'sortBy').length > 0;

  // Local sort/filter pipeline for new filters not handled by parent
  const processedHotels = useMemo(() => {
    let result = [...hotels];

    // Guest rating filter
    if (filters.guestRating) {
      const minRating = filters.guestRating;
      result = result.filter(h => {
        const rating = h.rating;
        return typeof rating === 'number' && rating >= minRating;
      });
    }

    // Free cancellation filter — applied if room data hints are available
    // (This is best-effort since cancellation data loads lazily per card)
    if (filters.freeCancellation) {
      // Keep all hotels — the filter signals intent but full data isn't available at list level
      // Individual cards will show/hide badges based on their lazy-loaded data
    }

    // Sort
    switch (filters.sortBy) {
      case 'price-asc':
        result.sort((a, b) => (a.price?.amount || Infinity) - (b.price?.amount || Infinity));
        break;
      case 'price-desc':
        result.sort((a, b) => (b.price?.amount || 0) - (a.price?.amount || 0));
        break;
      case 'rating':
        result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      // 'recommended' = default order from backend
    }

    return result;
  }, [hotels, filters.guestRating, filters.freeCancellation, filters.sortBy]);

  // VOY-490: map pins are now numbered teardrops matching list order
  // (was: price pills). Build an id→index lookup off the processed
  // (post-filter, post-sort) hotel list so map pin badges always agree
  // with what the user sees in the list. Hotels not in the list (e.g.
  // bounds-filtered-only on the map side) render with an empty badge.
  const hotelOrder = useMemo(() => {
    const m = new Map<string, number>();
    processedHotels.forEach((h, i) => m.set(h.id, i + 1));
    return m;
  }, [processedHotels]);

  const handleHotelHover = (hotelId: string | null) => { setHoveredHotelId(hotelId); };

  // Shared scroll + highlight. Silently no-ops if the card isn't in
  // the DOM (e.g. Map-only mode where the list isn't rendered) — the
  // explicit popup buttons handle that case by switching view first.
  const scrollToHotelCard = useCallback((hotelId: string) => {
    const hotelElement = document.getElementById(`hotel-${hotelId}`);
    if (!hotelElement) return;
    if (listPaneRef.current) {
      const container = listPaneRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = hotelElement.getBoundingClientRect();
      const target = container.scrollTop + (elementRect.top - containerRect.top) - 8;
      container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    } else {
      const rect = hotelElement.getBoundingClientRect();
      const currentScroll = window.scrollY || document.documentElement.scrollTop;
      const target = rect.top + currentScroll - 120;
      window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    }
    hotelElement.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-50');
    setTimeout(() => { hotelElement.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-50'); }, 2000);
  }, []);

  // Pin click → just attempts the scroll. NO view switch, NO popup
  // suppression. The marker popup is a separate affordance that
  // opens via the internal selectedHotelId state in hotel-map.tsx.
  // Behavior preserved verbatim from pre-VOY-490 (the prior "auto-
  // switch on marker click" path was wrong — it killed the popup
  // before the user could read it or click its buttons).
  const handleMarkerClick = useCallback((hotelId: string) => {
    scrollToHotelCard(hotelId);
  }, [scrollToHotelCard]);

  // EXPLICIT "View in list" CTA from the marker popup. This is the
  // only place the auto-switch from Map-only → Both fires — the user
  // is asking to see the card in the list, so we switch view modes
  // if needed, then scroll. The popup stays open during the switch
  // (because selectedHotelId stays set; popup re-mounts in the new
  // HotelMap instance with the same selection).
  const handleViewInList = useCallback((hotelId: string) => {
    if (viewMode === 'map' && onViewModeChange) {
      onViewModeChange('both');
      // Defer until the list pane mounts + lays out.
      setTimeout(() => scrollToHotelCard(hotelId), 120);
      return;
    }
    scrollToHotelCard(hotelId);
  }, [viewMode, onViewModeChange, scrollToHotelCard]);

  // EXPLICIT "View rooms" CTA from the marker popup. Same view-switch
  // gate, plus a chained click on the per-card view-rooms button
  // once the list has rendered.
  //
  // NOTE: the 500ms / 400ms timings below are EMPIRICAL (carried over
  // verbatim from the pre-VOY-490 window-event path that worked
  // reliably). They're load-timing guesses, not correctness
  // guarantees — could mis-fire on slow devices or under heavy load.
  // Resist the urge to shrink them without a real signal. A proper
  // long-term fix would key the chained click off a paint signal
  // (rAF / IntersectionObserver / `view-rooms-${id}` mutation
  // observer) instead of a wall-clock timeout.
  const handleViewRooms = useCallback((hotelId: string) => {
    const clickRoomsButton = () => {
      const btn = document.getElementById(`view-rooms-${hotelId}`) as HTMLButtonElement | null;
      btn?.click();
    };
    if (viewMode === 'map' && onViewModeChange) {
      onViewModeChange('both');
      // 120ms for list mount + 380ms for any HotelCard async work
      // (image load, lazy room data) — empirical 500ms total.
      setTimeout(() => {
        scrollToHotelCard(hotelId);
        clickRoomsButton();
      }, 500);
      return;
    }
    scrollToHotelCard(hotelId);
    // Empirical 400ms — original window-event path's buffer.
    setTimeout(clickRoomsButton, 400);
  }, [viewMode, onViewModeChange, scrollToHotelCard]);

  // VOY-490: the prior `hotel-view-rooms` window-event listener has
  // been replaced by the explicit `onViewRooms` prop on HotelMap →
  // marker popup buttons call `handleViewRooms` directly. Cleaner
  // than cross-component pub/sub and avoids the stale-closure trap
  // (the old listener was bound with empty deps and captured the
  // initial handleMarkerClick).

  // Get tier label for VIEWING AS badge
  const getTierLabel = () => {
    if (!isAuthenticated || !isPlanKnown) return null;
    if (isSubscriber) return 'SUBSCRIBER';
    return 'REGISTERED (FREE TIER)';
  };

  // Skeleton loading
  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden animate-pulse">
            <div className="flex gap-0">
              <div className="w-[280px] h-[220px] bg-gray-200 flex-shrink-0" />
              <div className="flex-1 p-4 space-y-3">
                <div className="h-6 bg-gray-200 rounded w-2/3" />
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="flex gap-2 mt-3">
                  <div className="h-6 w-16 bg-gray-200 rounded-full" />
                  <div className="h-6 w-20 bg-gray-200 rounded-full" />
                  <div className="h-6 w-14 bg-gray-200 rounded-full" />
                </div>
                <div className="flex items-center justify-between mt-auto pt-4">
                  <div className="h-8 bg-gray-200 rounded w-32" />
                  <div className="h-8 bg-gray-200 rounded w-24" />
                </div>
              </div>
            </div>
          </div>
        ))}
        <div className="text-center text-sm text-gray-500 mt-6 flex items-center justify-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500" />
          Searching for the best hotels...
        </div>
      </div>
    );
  }

  if (hotels.length === 0) {
    return (
      <div className="space-y-6">
        <Card className="border-gray-200">
          <CardContent className="p-8">
            <div className="text-center space-y-4">
              <div className="text-gray-500">
                {hasActiveFilters
                  ? "No hotels match your filters. Try adjusting or clearing them."
                  : "No hotels found for your search criteria."}
              </div>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={handleClearFilters} className="rounded-full">
                  Clear all filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        {suggestions.length > 0 && (
          <Card className="border-gray-200">
            <CardContent className="p-6">
              <div className="mb-3 font-medium">Did you mean one of these destinations?</div>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <Button key={String(s.id)} variant="outline" size="sm" onClick={() => onSelectSuggestion?.(s)}>
                    {s.name}{s.country_code ? `, ${s.country_code}` : ''}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {widened.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm text-gray-500 px-1">Nearby options</div>
            {widened.map((hotel) => (
              <HotelCard
                key={hotel.id}
                hotel={hotel}
                onSelect={() => {}}
                checkin={hotelCheckin}
                checkout={hotelCheckout}
                guests={hotelGuests || [{ adults: 2, children: [] }]}
                allHotels={hotels}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Render hotel list with CTA injection
  const renderHotelList = (hotelList: Hotel[], showCTA: boolean = true) => {
    const items: React.ReactNode[] = [];
    hotelList.forEach((hotel, index) => {
      items.push(
        <div
          key={hotel.id}
          id={`hotel-${hotel.id}`}
          onMouseEnter={() => handleHotelHover(hotel.id)}
          onMouseLeave={() => handleHotelHover(null)}
          className={cn(
            "transition-all duration-200 rounded-xl",
            hoveredHotelId === hotel.id && 'ring-2 ring-blue-400 shadow-lg'
          )}
        >
          <HotelCard
            hotel={hotel}
            onSelect={() => {}}
            checkin={hotelCheckin}
            checkout={hotelCheckout}
            guests={hotelGuests || [{ adults: 2, children: [] }]}
            allHotels={hotelList}
            // VOY-488: top-ranked hotel gets the gold "VOY PICK" pill,
            // but only when the user hasn't filtered down (otherwise
            // the global "best" claim is misleading against filter
            // state).
            featured={index === 0 && !hasActiveFilters}
          />
        </div>
      );

      // Insert CTA banner after card 2 (index 1) for non-subscribers
      if (showCTA && index === 1 && !isSubscriber && isPlanKnown && hotelList.length > 2) {
        items.push(<SubscribeCTABanner key="subscribe-cta" />);
      }
    });
    return items;
  };

  return (
    <div className="space-y-4">
      {/* Header: Count + Sort + View Toggle */}
      <div className={cn(
        "flex items-center justify-between flex-wrap gap-3",
        viewMode === 'list' ? 'max-w-5xl mx-auto' : ''
      )}>
        <div className="flex items-center gap-3">
          <span className="text-base font-medium text-gray-900">
            {hotelTotal || hotels.length} hotels found
          </span>
          {/* VIEWING AS badge */}
          {getTierLabel() && (
            <span className="text-[11px] tracking-wider text-gray-400 font-medium uppercase">
              VIEWING AS: {getTierLabel()}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* VOY-489: sort moved into the pill-strip FilterRowHotels
              below. Keeping only the view-mode toggle here. */}

          {/* VOY-466: Match-My-Taste toggle. Only renders when the
              parent threaded an onPersonalizeChange callback — the
              MVP integration site is LandingV6, which owns the
              pipeline state. Anon users still see it (component
              handles the "Sign in to personalise" CTA internally). */}
          {onPersonalizeChange && (
            <MatchMyTasteToggle
              enabled={personalizeEnabled}
              onChange={onPersonalizeChange}
              authed={!!user}
            />
          )}

          {/* View Mode Toggle */}
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-full p-0.5">
            <button
              onClick={() => onViewModeChange?.('list')}
              className={cn(
                "h-8 px-3 rounded-full text-sm transition-all duration-200 inline-flex items-center gap-1",
                viewMode === 'list'
                  ? "bg-white text-gray-900 shadow-sm font-medium"
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">List</span>
            </button>
            <button
              onClick={() => onViewModeChange?.(viewMode === 'both' ? 'map' : 'both')}
              className={cn(
                "h-8 px-3 rounded-full text-sm transition-all duration-200 inline-flex items-center gap-1",
                (viewMode === 'both' || viewMode === 'map')
                  ? "bg-white text-gray-900 shadow-sm font-medium"
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              <MapIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{viewMode === 'both' ? 'Map only' : 'Map'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* VOY-489: pill-strip filter row replaces the prior HotelFilterBar
          panel. Sort lives at the right end of the row. */}
      <div className={cn(viewMode === 'list' ? 'max-w-5xl mx-auto' : '')}>
        <FilterRowHotels
          filters={filters}
          onChange={handleFiltersChange}
          onClear={handleClearFilters}
          disabled={isLoading}
          priceMin={minPriceBound}
          priceMax={maxPriceBound}
          availableAmenities={availableAmenities}
        />
      </div>

      {/* Hotel Results */}
      {viewMode === 'list' && (
        <div className="max-w-5xl mx-auto space-y-4">
          {renderHotelList(processedHotels)}

          {/* Pagination — VOY-493: shared <Pager> pill-strip per design. */}
          {(hotelTotal && hotelPerPage && hotelPage && hotelTotalPages && onHotelPageChange) && (
            <Pager
              page={hotelPage}
              totalPages={hotelTotalPages}
              perPage={hotelPerPage}
              total={hotelTotal}
              onPageChange={onHotelPageChange}
            />
          )}

          {/* Pricing Legend */}
          <PricingLegend scope="hotel" />
        </div>
      )}

      {/* VOY-490: map-only mode wraps the MapLibre canvas in the
          shared `.map-card` chrome (bordered surface, 16px radius,
          surface-card bg) so it matches the Search+ design. The
          explicit height utility classes are kept to override the
          design's 520px min-height for a more useful full-bleed map. */}
      {viewMode === 'map' && MAP_ENABLED && (
        <div className="map-card h-[90vh]">
          <HotelMap
            hotels={hotels}
            onMarkerClick={handleMarkerClick}
            onMarkerHover={handleHotelHover}
            hoveredHotelId={hoveredHotelId}
            filters={filters}
            hotelOrder={hotelOrder}
            onViewInList={handleViewInList}
            onViewRooms={handleViewRooms}
          />
        </div>
      )}
      {viewMode === 'map' && !MAP_ENABLED && (
        <Card><CardContent className="p-8 text-center text-gray-500">Map is disabled by feature flag.</CardContent></Card>
      )}

      {/* VOY-490: "Both" view uses the design's `.map-shell` 2-col
          grid (collapses to single column <1100px) + the `.map-card`
          chrome on the map pane. Sticky behavior is now baked into
          `.map-card` (top: 80px) — no need for a separate sticky
          wrapper. Mobile keeps the existing "map first, list below"
          ordering via order utilities.

          VOY-490 review-fix: the order swap MUST happen at the same
          breakpoint where `.map-shell` becomes 2-col (1100px), not at
          Tailwind's xl: (1280px). Without this, the 1100–1280px
          window had a 2-col grid with REVERSED order (map left, list
          right). Using arbitrary breakpoint syntax to match the CSS
          media query exactly. */}
      {viewMode === 'both' && (
        <div className="map-shell">
          <div className="space-y-4 order-2 [@media(min-width:1100px)]:order-1">
            <div ref={listPaneRef} className="h-[75vh] sm:h-[80vh] xl:h-[92vh] overflow-y-auto pr-2 space-y-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {renderHotelList(processedHotels, false)}
            </div>
          </div>
          {MAP_ENABLED ? (
            <div className="map-card order-1 [@media(min-width:1100px)]:order-2 h-[75vh] sm:h-[85vh] xl:h-[92vh]">
              <HotelMap
                hotels={hotelMapHotels}
                onMarkerClick={handleMarkerClick}
                onMarkerHover={handleHotelHover}
                hoveredHotelId={hoveredHotelId}
                filters={hotelMapFilters}
                onBoundsChanged={handleBoundsChanged}
                hotelOrder={hotelOrder}
                onViewInList={handleViewInList}
                onViewRooms={handleViewRooms}
              />
            </div>
          ) : (
            <div className="order-1 [@media(min-width:1100px)]:order-2">
              <Card><CardContent className="p-8 text-center text-gray-500">Map is disabled by feature flag.</CardContent></Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
